import { useEffect, useState } from "react";
import { AUTH_TENANT_BRANDING } from "@/utils/const";
import type { TenantBrandingResponse } from "@shared-domain/auth-service/contract";

/**
 * PRODUCT_DEV-641 ログイン画面に出すテナントのブランド（ロゴと ID 欄の呼び方）を引く。
 *
 * - **tenantId が確定していないときは1本もリクエストを出さない。** 素のログイン画面
 *   （どのテナントに入るかまだ決まっていない）は従来と完全に同じ挙動にする。
 * - 取れなかったとき・未設定のときは null。**呼び出し側はこの2つを区別しない**
 *   （サーバも区別できる情報を返さない）。
 * - 署名 URL はタブ内で使い回す。ロゴのキーはイミュータブルなので期限内は再利用できる
 *   （`@shared-ui/utils/avatarCache` と同じ考え方。1テナント1件なので Map は持たない）。
 */

export type TenantBranding = {
  logoUrl: string | null;
  loginIdLabel: string | null;
  /** PRODUCT_DEV-1238 メールの無い人が「管理者に発行を依頼する」を使えるか（会社の設定）。 */
  passwordReissueEnabled: boolean;
};

const EMPTY: TenantBranding = { logoUrl: null, loginIdLabel: null, passwordReissueEnabled: false };

type CacheEntry = { tenantId: string; value: TenantBranding; expiresAt: number };

// モジュールレベル＝タブ（セッション）生存中は保持。
let cached: CacheEntry | null = null;
/**
 * **同じテナントへの取得中プロミス。**
 *
 * この hook は `AuthPageLayout`（ロゴ用）と `LoginForm`（呼び方用）の**両方**から
 * 同じ tenantId で呼ばれる。初回はどちらもキャッシュ未命中なので、これが無いと
 * 同じリクエストが 2 本飛ぶ（未認証エンドポイントなので特に無駄）。
 */
let inFlight: { tenantId: string; promise: Promise<TenantBranding> } | null = null;
// presigned の期限は 1h。切れる手前で捨てる。
const CACHE_TTL_MS = 30 * 60 * 1000;

const readCache = (tenantId: string | null): TenantBranding | null =>
  tenantId && cached && cached.tenantId === tenantId && cached.expiresAt > Date.now()
    ? cached.value
    : null;

/**
 * **「無い」と「取れなかった」を分ける。**
 *
 * どちらも画面では同じ（ブランドを出さない）が、**キャッシュしてよいのは「無い」だけ**。
 * 取得失敗まで 30 分キャッシュすると、Lambda の一時的な 5xx や API デプロイ中の 1 回で
 * そのタブは 30 分ロゴも呼び方も出さなくなる。しかも症状が「設定していないように見える」
 * ので原因が読めない。
 */
type FetchResult = { ok: boolean; value: TenantBranding };

const fetchBranding = (tenantId: string): Promise<TenantBranding> => {
  if (inFlight && inFlight.tenantId === tenantId) return inFlight.promise;
  const attempt = (async (): Promise<FetchResult> => {
    try {
      // **Bearer を付けない。** この API は認証不要で、付ける相手も居ない。
      const res = await fetch(AUTH_TENANT_BRANDING, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId }),
      });
      if (!res.ok) return { ok: false, value: EMPTY };
      const body = (await res.json()) as TenantBrandingResponse;
      return {
        ok: true,
        value: {
          logoUrl: body?.logoUrl ?? null,
          loginIdLabel: body?.loginIdLabel ?? null,
          passwordReissueEnabled: body?.passwordReissueEnabled === true,
        },
      };
    } catch {
      // ブランドが出ないだけでログインは続けられる。握りつぶす。
      return { ok: false, value: EMPTY };
    }
  })();
  const promise = attempt.then((r) => r.value);
  inFlight = { tenantId, promise };
  void attempt.then((r) => {
    // 成功したときだけ畳む。失敗は次の機会に問い合わせ直す。
    if (r.ok) cached = { tenantId, value: r.value, expiresAt: Date.now() + CACHE_TTL_MS };
    if (inFlight?.tenantId === tenantId) inFlight = null;
  });
  return promise;
};

/** テスト用: キャッシュを空にする（`@shared-ui/utils/avatarCache` と同じ作法）。 */
export const _resetTenantBrandingCache = (): void => {
  cached = null;
  inFlight = null;
};

export const useTenantBranding = (tenantId: string | null): TenantBranding => {
  const [branding, setBranding] = useState<TenantBranding>(
    () => readCache(tenantId) ?? EMPTY,
  );

  useEffect(() => {
    if (!tenantId) {
      setBranding(EMPTY);
      return;
    }
    const hit = readCache(tenantId);
    if (hit) {
      setBranding(hit);
      return;
    }
    // アンマウント後に setState しない（ログイン中は画面が次々切り替わる）。
    let alive = true;
    void fetchBranding(tenantId).then((value) => {
      if (alive) setBranding(value);
    });
    return () => {
      alive = false;
    };
  }, [tenantId]);

  return branding;
};
